/**
 * Keyboard shortcuts (DASHBOARD.md §1.17), as a pure function so the table can be
 * tested without a DOM. Everything is ignored while typing in a field, with a
 * modifier held (so browser shortcuts keep working) or while a dialog is open.
 */

export type ShortcutAction =
  | 'next' | 'prev' | 'yes' | 'no' | 'instruct' | 'select'
  | 'go-operations' | 'go-activity' | 'go-scorecard' | 'go-audit'
  | 'help' | 'palette';

export interface KeyInfo { key: string; ctrlKey?: boolean; metaKey?: boolean; altKey?: boolean; shiftKey?: boolean }

export interface KeyContext {
  /** The event came from an input, textarea, select or contenteditable. */
  typing: boolean;
  /** A dialog, drawer or the palette is open. */
  modalOpen: boolean;
  /** `g` was pressed less than GO_WINDOW_MS ago. */
  pendingGo: boolean;
}

export interface KeyResult { action: ShortcutAction | null; pendingGo: boolean; preventDefault: boolean }

export const GO_WINDOW_MS = 1200;

const SINGLE: Record<string, ShortcutAction> = { j: 'next', k: 'prev', y: 'yes', n: 'no', i: 'instruct', x: 'select' };
const GO: Record<string, ShortcutAction> = { o: 'go-operations', a: 'go-activity', s: 'go-scorecard', t: 'go-audit' };

export function interpretKey(e: KeyInfo, ctx: KeyContext): KeyResult {
  const none: KeyResult = { action: null, pendingGo: false, preventDefault: false };
  const k = e.key;
  // ⌘K / Ctrl-K opens the palette even from inside a field; it is the one shortcut that does.
  if ((e.metaKey || e.ctrlKey) && !e.altKey && k.toLowerCase() === 'k') return { action: 'palette', pendingGo: false, preventDefault: true };
  if (e.metaKey || e.ctrlKey || e.altKey) return none;
  if (ctx.typing || ctx.modalOpen) return none;
  if (ctx.pendingGo) {
    const g = GO[k.toLowerCase()];
    return g ? { action: g, pendingGo: false, preventDefault: true } : none;
  }
  if (k === '/') return { action: 'palette', pendingGo: false, preventDefault: true };
  if (k === '?') return { action: 'help', pendingGo: false, preventDefault: true };
  if (e.shiftKey) return none; // J, Y and the like are not shortcuts
  if (k === 'g') return { action: null, pendingGo: true, preventDefault: false };
  const a = SINGLE[k];
  return a ? { action: a, pendingGo: false, preventDefault: true } : none;
}

export const SHORTCUT_HELP: { keys: string; does: string }[] = [
  { keys: '⌘K · Ctrl K · /', does: 'Search everything and run commands' },
  { keys: 'j / k', does: 'Next / previous Action Center row' },
  { keys: 'y / n', does: 'Yes / No on the focused row (Enter confirms)' },
  { keys: 'i', does: 'Instruct instead on the focused row; on a bot page, open the Inbox composer' },
  { keys: 'x', does: 'Select the focused row for a bulk decision' },
  { keys: 'g o · g a · g s · g t', does: 'Go to Operations · Activity · Scorecard · Audit trail' },
  { keys: '?', does: 'This help' },
  { keys: 'Esc', does: 'Close a dialog or drawer' },
];

/** True when the event target is somewhere text is typed. */
export function isTypingTarget(t: EventTarget | null): boolean {
  const el = t as (HTMLElement | null);
  if (!el || !el.tagName) return false;
  const tag = el.tagName.toLowerCase();
  if (tag === 'input') return !['checkbox', 'radio', 'button', 'submit', 'reset'].includes(String((el as HTMLInputElement).type || 'text').toLowerCase());
  return tag === 'textarea' || tag === 'select' || el.isContentEditable === true;
}
